import { FC, Fragment, useState } from 'react';
import { Button, Col, Divider, Form, Input, Row } from 'antd';
import { ArrowLeftOutlined, LoadingOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/App';
import {
	MonitoringDocumentCategory,
	MonitoringDocumentSearchCustomerParam,
	MonitoringDocumentSubCategory,
	monitoringDocumentGetMonitoringCustomerDetail,
	monitoringDocumentGetMonitoringCategoryDetail,
} from '@/libs/fleet-api/services/monitoring-document';
import FleetTitle from '@/libs/antd/components/Title';
import DataRow, { DataRowItem } from '@/libs/antd/components/DataRow';
import MonitoringDocumentCategoryDetail, {
	transformCategoryDetailResponse,
} from './MonitoringDocumentDetailPage.Category';
import MonitoringDocumentDocumentDetail from './MonitoringDocumentDetailPage.Document';
import { useUserData } from '@/libs/fleet-api/hooks';
import { useModals } from '@/components/modals/ModalProvider';

type View =
	| {
			type: 'category';
	  }
	| {
			type: 'document';
			data: {
				category: MonitoringDocumentCategory;
				subCategory: MonitoringDocumentSubCategory;
			};
	  };

const MonitoringDocumentDetailPage: FC<{
	customerId: string;
	param: MonitoringDocumentSearchCustomerParam;
}> = ({ customerId, param }) => {
	const userData = useUserData();
	const [form] = Form.useForm<{ keyword: string }>();
	const [keyword, setKeyword] = useState('');
	const [view, setView] = useState<View>({ type: 'category' });
	const [selectedSpfInfo, setSelectedSpfInfo] = useState<{
		spfNo: string;
		spfStatus: string;
	} | null>(null);
	const [selectedMppInfo, setSelectedMppInfo] = useState<{
		mppNo: string;
		agreementNo: string;
	} | null>(null);
	const { navigate } = useApp();
	const { openProspectDetail } = useModals();

	const { data, isLoading, refetch } = useQuery({
		queryKey: [
			'monitoringDocumentGetMonitoringCustomerDetail',
			customerId,
		] as const,
		queryFn: ({ queryKey }) => {
			const [, customerId] = queryKey;
			return monitoringDocumentGetMonitoringCustomerDetail(customerId);
		},
	});

	if (isLoading) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	const { customerName, bmppGroupName, categories } = data?.data ?? {};

	// Extract mklNo and prospectId from subCategory id if viewing financing approval document
	let prospectId: string | null = null;
	let mklNo: string | null = null;
	let currentCategory: MonitoringDocumentCategory | null = null;
	let fetchType: string = 'full';
	let categoryId: string | null = null;

	if (view.type === 'document') {
		currentCategory = view.data.category;
		categoryId = currentCategory.id;
		fetchType = currentCategory.fetchType || 'full';

		const subCategoryId = view.data.subCategory.id;
		const subCategoryLabel = view.data.subCategory.label;
		const normalizedCategoryId = categoryId?.toLowerCase();

		const isCompositeIdCategory =
			normalizedCategoryId === 'financing-approval-document' ||
			normalizedCategoryId === 'covenant-document';

		const isFinancingDisbursementDocument =
			normalizedCategoryId === 'financing-disbursement-document';

		if (isCompositeIdCategory && subCategoryId.includes('|')) {
			const [mkl, prospect] = subCategoryId.split('|');
			mklNo = mkl;
			prospectId = prospect;
		} else if (isCompositeIdCategory) {
			// Fallback: if no composite id, try to extract from label or use id as mklNo
			mklNo = subCategoryId;
		} else if (isFinancingDisbursementDocument) {
			// For financing-disbursement-document: id is prospectId, label is mklNo
			prospectId = subCategoryId;
			mklNo = subCategoryLabel;
		}
	}

	const mainInformationData: DataRowItem[] = [
		[
			['Nama Customer', customerName ?? '-'],
			['Group BMPP', bmppGroupName ?? '-'],
		],
	];

	// Add Prospect ID and MKL number for financing approval documents and financing-disbursement documents
	if (view.type === 'document' && prospectId && mklNo) {
		mainInformationData.push([
			[
				'Prospect ID',
				prospectId ? (
					<a
						href="#"
						onClick={(e) => {
							e.preventDefault();
							openProspectDetail(prospectId!);
						}}
						style={{
							color: '#1677ff',
							textDecoration: 'underline',
						}}
					>
						{prospectId}
					</a>
				) : (
					'-'
				),
			],
			['No. MKL', mklNo],
		]);
	}

	// Add SPF number and status for covenant documents
	if (
		view.type === 'document' &&
		categoryId?.toLowerCase() === 'covenant-document' &&
		selectedSpfInfo
	) {
		mainInformationData.push([
			['No. SPF', selectedSpfInfo.spfNo],
			['Status SPF', selectedSpfInfo.spfStatus],
		]);
	}

	// Add MPP number and Agreement number for financing-disbursement documents
	if (
		view.type === 'document' &&
		categoryId?.toLowerCase() === 'financing-disbursement-document' &&
		selectedMppInfo
	) {
		mainInformationData.push([
			['No. MPP', selectedMppInfo.mppNo],
			[
				'No. Kesepakatan Realisasi Pencairan',
				selectedMppInfo.agreementNo ?? '-',
			],
		]);
	}

	if (userData?.groupName === 'FRM') {
		// DO NOTHING
	} else if (userData?.groupName === 'FBDH') {
		mainInformationData.push([['FRM', data?.data.frmName ?? '-']]);
	} else if (userData?.groupName === 'CCA') {
		mainInformationData.push([
			['FRM', data?.data.frmName ?? '-'],
			['FBDH', data?.data.fbdhName ?? '-'],
		]);
	} else if (userData?.groupName === 'CCDH') {
		mainInformationData.push([
			['FRM', data?.data.frmName ?? '-'],
			['FBDH', data?.data.fbdhName ?? '-'],
		]);
		mainInformationData.push([['CCA', data?.data.ccaName ?? '-']]);
	} else {
		mainInformationData.push([
			['FRM', data?.data.frmName ?? '-'],
			['FBDH', data?.data.fbdhName ?? '-'],
		]);
		mainInformationData.push([
			['CCA', data?.data.ccaName ?? '-'],
			['CCDH', data?.data.ccdhName ?? '-'],
		]);
	}

	const handleRefresh = async () => {
		if (view.type === 'document' && view.data.category.fetchType === 'split') {
			try {
				const res = await monitoringDocumentGetMonitoringCategoryDetail(
					customerId,
					view.data.category.id,
				);
				if (res?.data) {
					const transformedSubCategories = transformCategoryDetailResponse(
						res.data,
						view.data.category.id,
					);

					const currentSubId = view.data.subCategory.id;
					const newSubCategory = transformedSubCategories.find(
						(sub) => sub.id === currentSubId,
					);

					if (newSubCategory) {
						setView((prev) => {
							if (prev.type !== 'document') return prev;
							return {
								...prev,
								data: {
									...prev.data,
									subCategory: newSubCategory,
								},
							};
						});
					}
				}
			} catch (error) {
				console.error('Failed to refresh category detail', error);
			}
		} else {
			refetch();
		}
	};

	return (
		<Fragment>
			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 20,
				}}
			>
				<div>
					<button
						style={{
							display: 'flex',
							height: 44,
							width: 44,
							fontSize: 24,
							border: 'none',
							alignItems: 'center',
							justifyContent: 'center',
							background: '#1557BC',
							color: 'white',
							cursor: 'pointer',
						}}
						onClick={() => {
							if (view.type === 'document') {
								setView({ type: 'category' });
								setKeyword('');
								form.resetFields();
								setSelectedSpfInfo(null);
								setSelectedMppInfo(null);
							} else {
								navigate('MonitoringDocumentListPage', { defaultParam: param });
							}
						}}
					>
						<ArrowLeftOutlined />
					</button>
				</div>
				<div style={{ flex: 1 }}>
					<FleetTitle>
						{view.type === 'document'
							? [view.data.category.label, view.data.subCategory.label].join(
									' / ',
								)
							: 'Monitoring Dokumen'}
					</FleetTitle>
				</div>
			</div>
			<Form
				form={form}
				onFinish={(value) => setKeyword(value.keyword)}
			>
				<Row gutter={[20, 20]}>
					<Col flex="auto">
						<Form.Item
							name="keyword"
							noStyle
						>
							<Input
								placeholder="Search Document/File"
								style={{
									display: 'block',
								}}
							/>
						</Form.Item>
					</Col>
					<Col>
						<Button
							block
							type="primary"
							htmlType="submit"
						>
							CARI
						</Button>
					</Col>
				</Row>
			</Form>
			<Divider />
			<div style={{ marginBottom: 20 }}>
				<FleetTitle level={3}>
					{view.type === 'document' ? 'All FIles' : 'All Documents'}
				</FleetTitle>
			</div>
			<DataRow items={mainInformationData} />
			<Divider />

			{view.type === 'category' ? (
				<MonitoringDocumentCategoryDetail
					keyword={keyword}
					categories={categories ?? []}
					customerId={customerId}
					onClick={(subCategory, category) => {
						setView({
							type: 'document',
							data: {
								category,
								subCategory,
							},
						});
						setKeyword('');
						form.resetFields();
						// Reset SPF info and MPP info when navigating to new document view
						setSelectedSpfInfo(null);
						setSelectedMppInfo(null);
					}}
				/>
			) : (
				<MonitoringDocumentDocumentDetail
					keyword={keyword}
					data={
						data?.data.categories
							.find((category) => category.id === view.data.category.id)
							?.subCategories.find(
								(sub) => sub.id === view.data.subCategory.id,
							) ?? view.data.subCategory
					}
					fetchType={fetchType}
					categoryId={categoryId ?? ''}
					onDownloadFinish={handleRefresh}
					customerId={customerId}
					onSpfSelected={(spfNo, spfStatus) => {
						setSelectedSpfInfo({ spfNo, spfStatus });
					}}
					onMppSelected={(mppNo, agreementNo) => {
						setSelectedMppInfo({ mppNo, agreementNo });
					}}
				/>
			)}
		</Fragment>
	);
};

export default MonitoringDocumentDetailPage;
